import { BackendUser } from 'common/types'
import { useNotification } from 'hooks/use-notification/useNotification'
import { useSecurity } from 'hooks/use-security/useSecurity'
import { useUserApi } from 'hooks/use-user-api/useUserApi'
import { useState, useEffect } from 'react'
import { useHistory } from 'react-router'
import passwordValidator from 'password-validator'
import validator from 'validator'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'

export const usePasswordChange = (cipher?: string) => {
  const { decrypt } = useSecurity()
  const { notify } = useNotification()
  const [vDetails, setVDetails] = useState<BackendUser>()
  const [currentPassword, setCurrentPassword] = useState<string>()
  const [password, setPassword] = useState('')
  const [passwordValid, setPasswordValid] = useState(false)
  const [error, setError] = useState('')
  const [passwordChange, setPasswordChange] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [confirmPasswordValid, setConfirmPasswordValid] = useState(false)
  const { fetchUserWithApiKey, changePassword } = useUserApi()
  const { logEventAnalytics } = useAnalytics()
  const { backendUser, backendPartner } = useAuth()

  const history = useHistory()

  const schema = new passwordValidator()
  schema
    .is()
    .min(8)
    .is()
    .max(100)
    .has()
    .uppercase(1)
    .has()
    .lowercase()
    .has()
    .digits(1)
    .has()
    .not()
    .spaces()
    .has()
    .symbols(1)

  useEffect(() => {
    const getData = async () => {
      if (cipher) {
        const details = await decrypt(cipher)
        if (details) {
          const user = await fetchUserWithApiKey(details['id'])
          setVDetails(user)
          setShowChangePassword(true)
        } else {
          setError('Unable to verify your email')
        }
      }
    }
    getData()
  }, [])

  const onResetPassword = async () => {
    try {
      await changePassword(vDetails.id, password)
      notify('success', 'Successful, please login')
      setShowChangePassword(false)
      history.push('/')
    } catch (error) {
      setError(
        error === 'Invalid current password'
          ? 'Invalid current password'
          : 'This password has been used before. Choose a different one'
      )
    }
  }

  const onResetPasswordWithUser = async (id: string) => {
    try {
      const response = await changePassword(id, password, currentPassword)
      if (response) {
        setPasswordChange(true)
        notify('success', 'Successfully changed password')
        setShowChangePassword(false)
        history.push('/feed')

        const partnerLog = {}
        const userLog = {}
  
        userLog['User ID'] = backendUser?.id
  
        if (backendUser?.partnerId) {
          partnerLog['Partner ID'] = backendPartner?.id
          partnerLog['Company Name'] = backendPartner?.name
          partnerLog['Account Type'] = 'Partner'
        } else {
          userLog['Account Type'] = backendUser?.role
        }
        logEventAnalytics(
          backendUser?.partnerId ? 'Partner' : 'Member',
          'Change Password from Profile menu',
          'mixpanel',
          'Change Password from Profile menu',
          { ...partnerLog, ...userLog, 'Email address': backendUser?.username },
          backendUser?.id
        )
      }
    } catch (error) {
      setError(
        error === 'Invalid current password'
          ? 'Invalid current password'
          : 'This password has been used before. Choose a different one'
      )
    }
  }

  const onCancel = async () => {
    history.push('/')
  }

  const handleErrorMessage = (): string => {
    if (error.length) {
      return error === 'Invalid current password'
        ? 'Invalid current password'
        : 'This password has been used before. Choose a different one'
    }
    return ''
  }

  const onCurrentPasswordChange = (e: any) => {
    setError('')
    const value = e.target.value
    setCurrentPassword(value)
    const test = schema.validate(value) as boolean
    setPasswordValid(test)
  }

  const onPasswordChange = (e: any) => {
    setError('')
    const value = e.target.value
    setPassword(value)
    const test = schema.validate(value) as boolean
    setPasswordValid(test)
    const test2 = validator.equals(value, confirmPassword)
    setConfirmPasswordValid(test2)
  }

  const onConfirmPasswordChange = (e: any) => {
    setError('')
    const value = e.target.value
    setConfirmPassword(value)
    const test = validator.equals(value, password)
    setConfirmPasswordValid(test)
  }

  return {
    vDetails,
    error,
    setError,
    onResetPassword,
    onResetPasswordWithUser,
    password,
    setPassword,
    passwordValid,
    setPasswordValid,
    onCancel,
    passwordChange,
    setPasswordChange,
    showChangePassword,
    setShowChangePassword,
    history,
    currentPassword,
    setCurrentPassword,
    handleErrorMessage,
    confirmPassword,
    setConfirmPassword,
    confirmPasswordValid,
    setConfirmPasswordValid,
    onCurrentPasswordChange,
    onPasswordChange,
    onConfirmPasswordChange
  } as const
}
